import { ReactElement, useMemo } from 'react'
import { Box3, ColorRepresentation, Sphere } from 'three'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { Instances } from '@react-three/drei'
import { ThreeEvent } from '@react-three/fiber'
import InstancedBoxInstance from '../InstancedBoxInstance'
import { ConnectorKind, UNIT_BOX_GEOMETRY_ARGS } from '../utils'

export interface BoxInstanceData {
  position: ImmutableVector3
  domain: Domain
  relativePosition: number
  connectorKind: ConnectorKind
  isTarget?: boolean
}

interface Props {
  instances: BoxInstanceData[]
  color: ColorRepresentation
  onClick?: (event: ThreeEvent<MouseEvent>, index: number) => void
  hiddenElementGuids?: string[]
  getElementGuid?: (index: number) => string | undefined
}

const InstancedBoxMesh = ({
  instances,
  color,
  onClick,
  hiddenElementGuids,
  getElementGuid,
}: Props): ReactElement | null => {
  const [boundingBox, boundingSphere] = useMemo(() => {
    const box = new Box3()
    const sphere = new Sphere()

    instances.forEach(({ position }) => {
      box.expandByPoint(position.v)
      sphere.expandByPoint(position.v)
    })

    return [box, sphere]
  }, [instances])

  if (instances.length === 0) return null

  return (
    <Instances boundingBox={boundingBox} boundingSphere={boundingSphere}>
      <boxGeometry attach="geometry" args={UNIT_BOX_GEOMETRY_ARGS} />
      <meshStandardMaterial color={color} opacity={0.5} transparent={true} />

      {instances.map((instance, index) => {
        const elementGuid = getElementGuid?.(index)

        return (
          <InstancedBoxInstance
            key={index}
            {...instance}
            color={color}
            onClick={onClick ? event => onClick(event, index) : undefined}
            visible={
              !hiddenElementGuids || !elementGuid || !hiddenElementGuids.includes(elementGuid)
            }
          />
        )
      })}
    </Instances>
  )
}

export default InstancedBoxMesh
